/* eslint-disable jsx-a11y/alt-text */
import { Button, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InputSearch from '../../common/InputSearch';
import { Spinner } from '../../common/Spinner/Spinner';
import ArrowForwardIcon from '../../../icons/arrow_forward.svg';
import ArrowBackIcon from '../../../icons/arrow_back.svg';

import styles from './Series.module.scss';
import { postGetAllSeriesRequest } from './services/actions';
import { seriesSelector } from './services/selector';
import ErrorResponse from '../../shared/ErrorResponse';
import { useNavigate } from 'react-router-dom';
import { SeriesTable } from './components/SeriesTable';
import { calendarStyleDisabled, selectStyle } from '../Receipts/muiStyles/muiStyles';
import { disabledInputStyle, inputStyle } from '../Receipts/services/style';

const calendarStyle = {
  '.MuiInputBase-formControl': {
    backgroundColor: '#ffffff !important',
    height: 30,
    fontSize: '13px !important',
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: '#B0BEC5 !important',
    fontSize: '13px !important',
  },
  '.MuiSvgIcon-root': {
    fontSize: '18px !important',
  },
};

const formatDate = (date: Date, day: string = '01') => moment(date).format(`YYYY-MM-${day}`);

export const Series = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [viewType, setViewType] = useState<number>(1);
  const [seriesValue, setSeriesValue] = useState<any>('');
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [tableData, setTableData] = useState<any[]>([]);

  const { seriesList, seriesListErrorResponse, unreserveMarkingCodeErrorResponse, loading } =
    useSelector(seriesSelector);

  const handleMonthChange = (increment: number) => {
    setCalendarDate((prevDate) => moment(prevDate).add(increment, 'months').toDate());
  };

  const fetchSeriesData = () => {
    dispatch(
      postGetAllSeriesRequest(
        viewType === 1
          ? {
              fromDate: formatDate(calendarDate, '01'),
              toDate: formatDate(calendarDate, moment(calendarDate).daysInMonth().toString()),
            }
          : {
              series: seriesValue,
            },
      ),
    );
  };

  useEffect(() => {
    setTableData(seriesList);
  }, [seriesList]);

  useEffect(() => {
    const errorResponse = unreserveMarkingCodeErrorResponse || seriesListErrorResponse;
    if (errorResponse?.status === 403) {
      navigate('/error');
    }
  }, [unreserveMarkingCodeErrorResponse, seriesListErrorResponse, navigate]);

  const renderError = (error: any) => (Object.keys(error).length ? <ErrorResponse error={error} /> : null);

  return (
    <div className={styles.wrapper}>
      {renderError(unreserveMarkingCodeErrorResponse)}
      {renderError(seriesListErrorResponse)}

      <section className={styles.header}>
        <div className={styles.group}>
          <span className={styles.subtitle}>Звіт по серіям</span>
          {loading && <Spinner />}
        </div>
        <span>Детальний перегляд друку маркувань, агрегацій та відвантажень по серіям</span>
      </section>

      <section className={styles.block}>
        <div className={styles.tools}>
          <div className={styles.calendarBlock}>
            <div className={styles.calendarItem}>
              <span>Пошук по:</span>
              <Select
                sx={selectStyle}
                onChange={(e: SelectChangeEvent<any>) => {
                  setViewType(e.target.value);
                  setSeriesValue('');
                }}
                value={viewType}
              >
                <MenuItem sx={{ fontSize: '13px' }} value={1}>
                  Дата
                </MenuItem>
                <MenuItem sx={{ fontSize: '13px' }} value={2}>
                  Серія
                </MenuItem>
              </Select>
            </div>

            <div className={styles.calendarItem}>
              <span>Дані за пeріод:</span>
              <div className={styles.calendarRow}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    inputFormat="MM/YYYY"
                    value={calendarDate}
                    views={['year', 'month']}
                    onChange={(newDate) => setCalendarDate(new Date(newDate!))}
                    disabled={viewType !== 1}
                    renderInput={(params) => (
                      <TextField sx={viewType !== 1 ? calendarStyleDisabled : calendarStyle} {...params} />
                    )}
                  />
                </LocalizationProvider>
                <img src={ArrowBackIcon} onClick={() => handleMonthChange(-1)} />
                <img src={ArrowForwardIcon} onClick={() => handleMonthChange(1)} />
              </div>
            </div>

            <div className={styles.calendarItem}>
              <span>Серія:</span>
              <OutlinedInput
                name="Series"
                value={seriesValue}
                onChange={(event) => {
                  setSeriesValue(event.target.value.trim());
                }}
                sx={viewType === 1 ? disabledInputStyle : inputStyle}
                disabled={viewType === 1}
                inputProps={{ maxLength: 20 }}
              />
            </div>

            <Button variant="contained" className={styles.btn} onClick={fetchSeriesData}>
              <span className={styles.btnText}>Пошук</span>
            </Button>
          </div>
        </div>

        <div className={styles.searchBlock}>
          <span>Фільтр по всім полям:</span>
          <InputSearch setTableData={setTableData} data={seriesList} clearOnPropsChange />
        </div>
      </section>

      <SeriesTable
        data={tableData}
        dateParams={
          viewType === 1
            ? {
                fromDate: formatDate(calendarDate, '01'),
                toDate: formatDate(calendarDate, moment(calendarDate).daysInMonth().toString()),
              }
            : {
                series: seriesValue,
              }
        }
        isFileDownloading={false}
      />
    </div>
  );
};
